import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { Box } from '@src/components';
import { useAuthentication } from '@src/context/hooks';
import { toTime } from '@src/utils';
import { useCallback, useMemo, useRef, useState } from 'react';

import { ABS_DAY_VIEW, DB_CLICK_THRESHHOLD } from '../../constants';
import useAbsencesTimeline from '../../hooks';
import {
    absencesToCalendarEvents,
    getSlotDataFromEvent,
    getSlotDatefromSelection,
    getTransientEventsForSlot,
    usersToCalendarResources,
} from '../../utils';
import { Header } from './components';

export default function Timeline() {
    const lastDatesRef = useRef(null);
    const lastClickRef = useRef({ id: null, time: 0 });

    const [currentDates, setCurrentDates] = useState({
        start: new Date(),
        end: new Date(),
    });

    const { user } = useAuthentication();
    const {
        absences,
        users,
        view,
        setView,
        selectedSlot,
        setSelectedSlot,
        attachCalendar,
        calendar,
        isMonthOrHalfMonthView,
    } = useAbsencesTimeline();

    const timelineResources = useMemo(() => {
        const resources = usersToCalendarResources(users, user);
        return resources;
    }, [user, users]);

    const timelineEvents = useMemo(() => {
        return [
            ...absencesToCalendarEvents(
                selectedSlot,
                absences,
                !isMonthOrHalfMonthView,
            ),
            ...getTransientEventsForSlot(
                selectedSlot,
                absences,
                users,
                !isMonthOrHalfMonthView,
            ),
        ];
    }, [users, absences, selectedSlot, isMonthOrHalfMonthView]);

    const onSelect = (info) => {
        const slotData = getSlotDatefromSelection(info);
        setSelectedSlot(slotData);
    };
    const onAbsenceClick = useCallback(
        (info) => {
            const now = Date.now();
            const id = info.event.id;
            const { id: prevId, time: prevTime } = lastClickRef.current;
            const isSame = prevId === id;
            const isDbl = isSame && now - prevTime < DB_CLICK_THRESHHOLD;

            if (isDbl) {
                const slot = getSlotDataFromEvent(info.event, absences, {
                    sameType: (a, b) => a.acti_type === b.acti_type,
                    single: !isMonthOrHalfMonthView ? true : false,
                });
                setSelectedSlot(slot);
            } else {
                const slot = getSlotDataFromEvent(info.event, absences, {
                    single: true,
                });
                setSelectedSlot(slot);
            }

            lastClickRef.current = { id, time: now };
        },
        [absences, setSelectedSlot, isMonthOrHalfMonthView],
    );

    const getAbsenceBaseClasses = (absence) => {
        const pos = absence.extendedProps.block_position;
        const classes = ['abs-base'];
        if (pos && isMonthOrHalfMonthView) {
            classes.push(`abs-${pos}`);
        }
        if (!absence.allDay && isMonthOrHalfMonthView) {
            classes.push('abs-partial');
        }
        return classes;
    };
    const getAbsenceSelectedClasses = (absence) => {
        if (selectedSlot) {
            const selectedIds = selectedSlot.absences_ids;
            if (selectedIds.includes(parseInt(absence.id))) {
                return ['abs-selected'];
            }
        }
    };
    const eventClassNames = (arg) => {
        const absence = arg.event;
        const base_absence_cls = getAbsenceBaseClasses(absence);
        const selected_absence_cls = getAbsenceSelectedClasses(absence);
        return [...base_absence_cls, selected_absence_cls];
    };

    const handleDatesSet = (arg) => {
        const newStart = arg.start.getTime();
        const newEnd = arg.end.getTime();

        const { start: lastStart, end: lastEnd } = lastDatesRef.current || {};
        if (newStart !== lastStart && newEnd !== lastEnd) {
            const newDates = { start: arg.start, end: arg.end };
            lastDatesRef.current = newDates;
            setCurrentDates(newDates);
        }
        if (view?.type !== arg.view.type) {
            setView(arg.view);
        }
    };
    const onSlotLaneClassNames = (arg) => {
        const day = arg.date.getDay();
        const isWeekend = day === 0 || day === 6;

        const today = new Date();
        const isToday =
            arg.date.getFullYear() === today.getFullYear() &&
            arg.date.getMonth() === today.getMonth() &&
            arg.date.getDate() === today.getDate();

        return [isWeekend && 'fc-weekend-slot', isToday && 'fc-today-slot'].filter(
            Boolean,
        );
    };

    const onNavLinkDay = useCallback(
        (date) => {
            calendar.changeView(ABS_DAY_VIEW, date);
        },

        [calendar],
    );

    const setCalendarRef = useCallback(
        (node) => {
            attachCalendar(node);
        },
        [attachCalendar],
    );

    const onEventContent = (args) => {
        const label = args.event.extendedProps.label;
        if (label) {
            return {
                html: `<div class="abs-event-content">${label}</div>`,
            };
        }
        const show_workday_count = args.event.extendedProps.show_workday_count;
        if (show_workday_count) {
            const count = args.event.extendedProps.workday_count;
            return { html: `<div class="abs-workday-count">${count}</div>` };
        }
        return '';
    };
    const onEventResize = (info) => {
        setSelectedSlot((slot) => ({
            ...slot,
            startTime: toTime(info.event.start),
            endTime: toTime(info.event.end),
        }));
    };

    return (
        <Box sx={StyleSheet.calendar}>
            <Header currentDates={currentDates} />

            <FullCalendar
                ref={setCalendarRef}
                height="900px"
                navLinks={true}
                select={onSelect}
                events={timelineEvents}
                datesSet={handleDatesSet}
                eventClick={onAbsenceClick}
                eventResize={onEventResize}
                navLinkDayClick={onNavLinkDay}
                eventClassNames={eventClassNames}
                initialView="resourceTimelineMonth"
                plugins={[resourceTimelinePlugin, interactionPlugin]}
                views={{
                    resourceTimelineMonth: {
                        selectable: true,
                        dayHeaderFormat: { month: 'numeric', day: 'numeric' },
                        slotLabelFormat: [{ day: 'numeric' }],
                    },
                    resourceTimelineHalfMonth: {
                        type: 'resourceTimeline',
                        selectable: true,
                        slotDuration: { days: 1 },
                        slotLabelFormat: [{ day: 'numeric' }],
                    },
                    resourceTimelineDay: {
                        selectable: true,
                        slotLabelFormat: [{ hour: 'numeric', hour12: false }],
                        slotMinWidth: 20,
                    },
                }}
                resourceAreaWidth={350}
                resourceOrder="order,title"
                resources={timelineResources}
                headerToolbar={{ left: '', right: '' }}
                slotLaneClassNames={onSlotLaneClassNames}
                eventContent={onEventContent}
                resourceAreaHeaderContent=""
            />
        </Box>
    );
}

const StyleSheet = {
    calendar: (theme) => ({
        flex: 1,
        width: '100%',
        padding: '70px 0 0 30px',
        '.fc-header-toolbar': {
            '.fc-toolbar-chunk > div': {
                display: 'flex',
                h2: { margin: '0 10px' },
            },
        },
        '.fc .fc-timeline-header-row-chrono .fc-timeline-slot-frame': {
            justifyContent: 'center',
        },
        '.fc-weekend-slot': {
            background: 'rgba(0, 0, 0, 0.05)',
        },

        '.fc-today-slot': (theme) => ({
            background: theme.palette.blue[120],
        }),

        '.fc-timeline-slot.fc-weekend-slot': {
            background: 'rgba(0, 0, 0, 0.08)',
        },
        '.abs-base': {
            height: '28px',
            padding: '0 6px',
        },
        '.abs-start': { borderRadius: '100px 0 0 100px' },
        '.abs-middle': { borderRadius: 0 },
        '.abs-end': { borderRadius: '0 100px 100px 0' },
        '.abs-single': { borderRadius: '100px' },
        '.abs-selected': {
            border: '3px dashed !important',
            borderColor: theme.palette.common.black,
        },
        '.abs-workday-count': {
            color: theme.palette.common.white,
            fontWeight: 'bold',
            fontSize: '14px',
            textAlign: 'center',
            lineHeight: '28px',
            width: '100%',
        },
        '.abs-partial': {
            background: 'transparent !important',
            border: '3px solid',
        },
        '.abs-selected.abs-partial': {
            background: `${theme.palette.grey[200]} !important`,
        },
        '.fc-event.transient-event': {
            opacity: 0.6,
            border: '1px solid',
            borderColor: theme.palette.common.black,
            background: theme.palette.grey[500],
        },
        '.abs-event-content': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 900,
            color: theme.palette.common.white,
        },
        '.fc-event.transient-event .abs-event-content': {
            color: theme.palette.common.black,
        },
    }),
};
